import React, { useCallback, useEffect, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';

import { useStaffAccess } from '../../../customRoutes/StaffRoute';
import AccountChecklist from '../AccountChecklist';
import {
  getEligibleCampaigns,
  getMyInvoices,
  markInvoicesSeen,
} from '../../../api_handlers/invoice';
import {
  Alert,
  BrandMark,
  Empty,
  ICONS,
  Icon,
  KIT_CSS,
  StatusPill,
  StatusTrack,
  money,
  shortDate,
  useCountdown,
  useResponsiveViewport,
  useStyleSheet,
} from '../../invoices/kit';
import InvoiceForm from './InvoiceForm';
import InvoiceViewer from '../../invoices/InvoiceViewer';
import NoticeStrip, { NOTICE_CSS } from '../../invoices/NoticeStrip';
import { AUDIENCE } from '../../invoices/lib/notifications';

const CSS = `
/* --- earnings hero ------------------------------------------------ */
.ivc-hero {
  background: linear-gradient(135deg, #0E2A31 0%, #123C46 48%, #14707F 100%);
  border-radius: 18px; padding: 26px 28px; color: #fff; position: relative; overflow: hidden;
  box-shadow: 0 18px 44px -22px rgba(18,60,70,.75);
}
.ivc-hero::after {
  content: ''; position: absolute; width: 320px; height: 320px; border-radius: 50%;
  top: -140px; right: -80px; background: radial-gradient(circle, rgba(30,156,178,.55) 0%, transparent 70%);
  pointer-events: none;
}
.ivc-hero__inner { position: relative; z-index: 1; }
.ivc-hero__label { font-size: 11.5px; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; color: rgba(255,255,255,.6); margin: 0 0 8px; }
.ivc-hero__figure { font-size: 40px; font-weight: 800; letter-spacing: -.03em; line-height: 1; margin: 0; }
.ivc-hero__note { font-size: 13.5px; color: rgba(255,255,255,.72); margin: 10px 0 0; }

.ivc-bar { display: flex; height: 8px; border-radius: 999px; overflow: hidden; background: rgba(255,255,255,.16); margin-top: 22px; }
.ivc-bar span { display: block; height: 100%; transition: width .5s cubic-bezier(.2,.8,.3,1); }
.ivc-legend { display: flex; flex-wrap: wrap; gap: 22px; margin-top: 16px; }
.ivc-legend__item { display: flex; align-items: center; gap: 8px; }
.ivc-legend__swatch { width: 8px; height: 8px; border-radius: 3px; flex: none; }
.ivc-legend__value { font-size: 15px; font-weight: 800; color: #fff; }
.ivc-legend__label { font-size: 12px; color: rgba(255,255,255,.62); }

/* --- ready-to-invoice grid ---------------------------------------- */
.ivc-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(330px, 1fr)); gap: 14px; }
.ivc-opp {
  background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius);
  padding: 18px 20px; display: flex; flex-direction: column; gap: 14px;
  position: relative; overflow: hidden; box-shadow: var(--shadow-sm);
  transition: border-color .18s ease, box-shadow .18s ease, transform .18s ease;
}
.ivc-opp::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 3px; background: linear-gradient(180deg, var(--accent), #6FD2E4); }
.ivc-opp:hover { border-color: var(--line-2); box-shadow: var(--shadow-md); transform: translateY(-2px); }
.ivc-opp__top { display: flex; gap: 13px; align-items: flex-start; }
.ivc-opp__name { font-size: 15px; font-weight: 800; color: var(--ink); margin: 0; line-height: 1.35; letter-spacing: -.01em; }
.ivc-opp__meta { font-size: 12.5px; color: var(--ink-3); margin: 3px 0 0; }
.ivc-opp__foot { display: flex; align-items: flex-end; justify-content: space-between; gap: 14px; margin-top: auto; }
.ivc-opp__amount { font-size: 22px; font-weight: 800; color: var(--ink); letter-spacing: -.02em; line-height: 1; }
.ivc-opp__amountLabel { font-size: 11px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: var(--ink-4); margin: 0 0 5px; }

/* --- invoice rows -------------------------------------------------- */
.ivc-row { padding: 18px 20px; border-bottom: 1px solid var(--line); transition: background .15s ease; }
.ivc-row:last-child { border-bottom: 0; }
.ivc-row:hover { background: #FCFDFD; }
.ivc-row__top { display: flex; gap: 14px; align-items: flex-start; }
.ivc-row__body { flex: 1 1 0; min-width: 0; }
.ivc-row__name { font-size: 15px; font-weight: 800; color: var(--ink); margin: 0; letter-spacing: -.01em; }
.ivc-row__meta { font-size: 12.5px; color: var(--ink-3); margin: 3px 0 0; }
.ivc-row__right { text-align: right; flex: none; display: flex; flex-direction: column; align-items: flex-end; gap: 7px; }
.ivc-row__amount { font-size: 19px; font-weight: 800; color: var(--ink); letter-spacing: -.02em; line-height: 1; }
.ivc-row__actions { display: flex; align-items: center; gap: 6px; margin-top: 14px; flex-wrap: wrap; }
.ivc-row__window { font-size: 12px; color: var(--ink-3); display: inline-flex; align-items: center; gap: 5px; }
.ivc-row__window strong { color: var(--accent-ink); font-weight: 800; }

.ivc-reject { margin-top: 13px; padding: 12px 14px; border-radius: var(--radius-sm); background: #FEF6F6; border: 1px solid #F6DCDC; }
.ivc-reject__label { font-size: 11px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: #B91C1C; margin: 0 0 4px; }
.ivc-reject__text { font-size: 13px; line-height: 1.55; color: #7F1D1D; margin: 0; }

.ivc-row__post { display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; font-weight: 700; color: var(--accent-ink); text-decoration: none; margin-top: 12px; }
.ivc-row__post:hover { text-decoration: underline; }

.ivc-updated { font-size: 10.5px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: #fff; background: var(--accent); border-radius: 999px; padding: 3px 8px; }
.ivc-lock { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 800; color: #15803D; background: #DCFCE7; border-radius: 999px; padding: 2px 8px; margin-left: 6px; vertical-align: middle; }

.ivc-paid { display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; color: #15803D; font-weight: 700; margin-top: 12px; }

@media (max-width: 720px) {
  .ivc-hero { padding: 22px 20px; }
  .ivc-hero__figure { font-size: 32px; }
  .ivc-grid { grid-template-columns: 1fr; }
  /* Keep the brand mark beside the campaign name; only the money block
     drops to its own line. */
  .ivc-row__top { flex-wrap: wrap; }
  .ivc-row__right { text-align: left; width: 100%; flex-direction: row; justify-content: space-between; align-items: center; margin-top: 4px; }
}
`;

function Skeletons() {
  return (
    <div className="iv-card">
      {[0, 1, 2].map((i) => (
        <div key={i} className="ivc-row">
          <div className="ivc-row__top">
            <div className="iv-sk" style={{ width: 42, height: 42, borderRadius: 11 }} />
            <div className="ivc-row__body">
              <div className="iv-sk" style={{ width: '42%', height: 15 }} />
              <div className="iv-sk" style={{ width: '62%', height: 12, marginTop: 8 }} />
            </div>
            <div className="iv-sk" style={{ width: 84, height: 22, borderRadius: 999 }} />
          </div>
          <div className="iv-sk" style={{ width: '100%', height: 15, marginTop: 18 }} />
        </div>
      ))}
    </div>
  );
}

function EarningsHero({ totals, count }) {
  const tracked = totals.paid + totals.flight;
  const pct = (part) => (tracked > 0 ? `${(part / tracked) * 100}%` : '0%');

  return (
    <div className="ivc-hero">
      <div className="ivc-hero__inner">
        <p className="ivc-hero__label">Paid to date</p>
        <p className="ivc-hero__figure iv-num">{money(totals.paid)}</p>
        <p className="ivc-hero__note">
          {totals.flight > 0
            ? `${money(totals.flight)} still moving through approval across ${count} invoice${
                count === 1 ? '' : 's'
              }.`
            : `Across ${count} invoice${count === 1 ? '' : 's'}. Nothing pending right now.`}
        </p>

        {tracked > 0 ? (
          <>
            <div className="ivc-bar" role="img" aria-label="Paid versus pending">
              <span style={{ width: pct(totals.paid), background: '#5CD68A' }} />
              <span style={{ width: pct(totals.flight), background: '#6FD2E4' }} />
            </div>
            <div className="ivc-legend">
              <span className="ivc-legend__item">
                <span className="ivc-legend__swatch" style={{ background: '#5CD68A' }} />
                <span className="ivc-legend__value iv-num">{money(totals.paid)}</span>
                <span className="ivc-legend__label">paid</span>
              </span>
              <span className="ivc-legend__item">
                <span className="ivc-legend__swatch" style={{ background: '#6FD2E4' }} />
                <span className="ivc-legend__value iv-num">{money(totals.flight)}</span>
                <span className="ivc-legend__label">in progress</span>
              </span>
              {totals.rejected > 0 ? (
                <span className="ivc-legend__item">
                  <span
                    className="ivc-legend__swatch"
                    style={{ background: 'rgba(255,255,255,.3)' }}
                  />
                  <span className="ivc-legend__value iv-num">{money(totals.rejected)}</span>
                  <span className="ivc-legend__label">rejected</span>
                </span>
              ) : null}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function Opportunity({ campaign, onRaise }) {
  return (
    <div className="ivc-opp">
      <div className="ivc-opp__top">
        <BrandMark
          name={(campaign.brand && campaign.brand.name) || campaign.name}
          src={campaign.brand && campaign.brand.brand_logo_url}
        />
        <div style={{ minWidth: 0 }}>
          <p className="ivc-opp__name">{campaign.name}</p>
          <p className="ivc-opp__meta">
            {campaign.brand && campaign.brand.name ? `${campaign.brand.name} · ` : ''}
            {campaign.status === 'completed' ? 'Ended' : 'Ends'} {shortDate(campaign.end_date)}
            {campaign.post_count ? ` · ${campaign.post_count} posts` : ''}
          </p>
        </div>
      </div>
      <div className="ivc-opp__foot">
        <div>
          <p className="ivc-opp__amountLabel">
            Agreed payout
            {campaign.price_locked ? <span className="ivc-lock">Locked</span> : null}
          </p>
          <div className="ivc-opp__amount iv-num">
            {campaign.agreed_amount ? money(campaign.agreed_amount) : '—'}
          </div>
        </div>
        <button type="button" className="iv-btn iv-btn--primary" onClick={() => onRaise(campaign)}>
          Raise invoice
          <Icon path={ICONS.arrow} size={14} />
        </button>
      </div>
    </div>
  );
}

function InvoiceRow({ invoice, canResubmit, onView, onEdit, onResubmit }) {
  const countdown = useCountdown(invoice.is_editable ? invoice.editable_until : null);

  return (
    <div className="ivc-row">
      <div className="ivc-row__top">
        <BrandMark name={invoice.brand_name || invoice.campaign_name} />
        <div className="ivc-row__body">
          <p className="ivc-row__name">{invoice.campaign_name}</p>
          <p className="ivc-row__meta">
            {invoice.brand_name ? `${invoice.brand_name} · ` : ''}
            {invoice.invoice_number ? `${invoice.invoice_number} · ` : ''}
            Raised {shortDate(invoice.submitted_at)}
          </p>
        </div>
        <div className="ivc-row__right">
          <div className="ivc-row__amount iv-num">{money(invoice.amount)}</div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {invoice.has_unseen_update ? <span className="ivc-updated">Updated</span> : null}
            <StatusPill status={invoice.status} label={invoice.status_display} />
          </div>
        </div>
      </div>

      <StatusTrack status={invoice.status} />

      {invoice.post_url ? (
        <a
          className="ivc-row__post"
          href={invoice.post_url}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Icon path={ICONS.arrow} size={12} />
          View the post this invoice is for
        </a>
      ) : null}

      {invoice.status === 'rejected' && invoice.remarks ? (
        <div className="ivc-reject">
          <p className="ivc-reject__label">Why it was rejected</p>
          <p className="ivc-reject__text">{invoice.remarks}</p>
        </div>
      ) : null}

      {invoice.status === 'paid' && invoice.paid_at ? (
        <div className="ivc-paid">
          <Icon path={ICONS.check} size={14} />
          Paid {shortDate(invoice.paid_at)}
          {invoice.payment_reference ? ` · ref ${invoice.payment_reference}` : ''}
        </div>
      ) : null}

      <div className="ivc-row__actions">
        {invoice.invoice_url ? (
          <button
            type="button"
            className="iv-btn iv-btn--ghost iv-btn--sm"
            onClick={() => onView(invoice)}
          >
            <Icon path={ICONS.eye} size={14} />
            View
          </button>
        ) : null}

        {invoice.is_editable ? (
          <>
            <button
              type="button"
              className="iv-btn iv-btn--ghost iv-btn--sm"
              onClick={() => onEdit(invoice)}
            >
              <Icon path={ICONS.pencil} size={14} />
              Edit
            </button>
            <span className="ivc-row__window">
              <Icon path={ICONS.clock} size={13} />
              {countdown ? (
                <>
                  editable for <strong>{countdown}</strong>
                </>
              ) : (
                'edit window closing'
              )}
            </span>
          </>
        ) : null}

        {canResubmit ? (
          <button
            type="button"
            className="iv-btn iv-btn--primary iv-btn--sm"
            onClick={() => onResubmit(invoice)}
          >
            Resubmit
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function InvoiceList() {
  useStyleSheet('iv-kit', KIT_CSS);
  useStyleSheet('ivc-page', CSS);
  useStyleSheet('nt-notices', NOTICE_CSS);
  useResponsiveViewport();

  const [eligible, setEligible] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [raising, setRaising] = useState(null);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const isStaff = useStaffAccess();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [eligibleResp, invoiceResp] = await Promise.all([
        getEligibleCampaigns(),
        getMyInvoices(),
      ]);
      setEligible(eligibleResp || []);
      setInvoices(invoiceResp || []);
      // The rows keep their "Updated" badge for this visit; the next visit
      // starts clean. Fire-and-forget — a failure here only means the
      // badges show again next time.
      if ((invoiceResp || []).some((invoice) => invoice.has_unseen_update)) {
        markInvoicesSeen().catch(() => {});
      }
    } catch (e) {
      setError(e.message || 'Could not load your invoices.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // A rejected invoice's campaign comes back in the eligible list, so the
  // row can offer a resubmit shortcut scoped to that campaign.
  const eligibleById = {};
  eligible.forEach((campaign) => {
    eligibleById[campaign.id] = campaign;
  });

  const totals = invoices.reduce(
    (acc, invoice) => {
      const amount = Number(invoice.amount) || 0;
      if (invoice.status === 'paid') acc.paid += amount;
      else if (invoice.status === 'rejected') acc.rejected += amount;
      else acc.flight += amount;
      return acc;
    },
    { paid: 0, flight: 0, rejected: 0 },
  );

  return (
    <div className="iv-root iv-page">
      <div className="iv-wrap iv-wrap--reading">
        <header className="iv-head">
          <div>
            <h1 className="iv-h1">Invoices &amp; payments</h1>
            <p className="iv-lede">
              Raise an invoice once your campaign is running or has wrapped, then watch it move
              through approval to payment — no chasing anyone for a status.
            </p>
          </div>
          {isStaff ? (
            <a className="iv-btn iv-btn--ghost" href="/planner/invoices">
              Staff console
              <Icon path={ICONS.arrow} size={14} />
            </a>
          ) : null}
        </header>

        {/* No `payoutOnFile` on purpose: the account checklist below already
            owns that chase, and asking twice on one screen is how a prompt
            gets ignored. This strip is for what happened to their invoices. */}
        <NoticeStrip audience={AUDIENCE.CREATOR} context={{ invoices }} />

        {error ? (
          <div style={{ marginBottom: 24 }}>
            <Alert onRetry={load}>{error}</Alert>
          </div>
        ) : null}

        {/* Setup steps belong here too: this is where a creator comes to get
            paid, and an incomplete account is what stops that happening. */}
        <AccountChecklist />

        {!loading && invoices.length > 0 ? (
          <EarningsHero totals={totals} count={invoices.length} />
        ) : null}

        <section className="iv-section">
          <div className="iv-section__bar">
            <h2 className="iv-h2">
              Ready to invoice
              {!loading && eligible.length > 0 ? (
                <span className="iv-h2__count">{eligible.length}</span>
              ) : null}
            </h2>
          </div>

          {loading ? (
            <div className="ivc-grid">
              {[0, 1].map((i) => (
                <div key={i} className="iv-sk" style={{ height: 148, borderRadius: 14 }} />
              ))}
            </div>
          ) : eligible.length === 0 ? (
            <div className="iv-card">
              <Empty title="Nothing to invoice right now">
                A campaign lands here once it is running or completed and you have been approved on
                it. Once you have raised an invoice, it moves down to the list below.
              </Empty>
            </div>
          ) : (
            <div className="ivc-grid">
              {eligible.map((campaign) => (
                <Opportunity key={campaign.id} campaign={campaign} onRaise={setRaising} />
              ))}
            </div>
          )}
        </section>

        <section className="iv-section">
          <div className="iv-section__bar">
            <h2 className="iv-h2">
              Your invoices
              {!loading && invoices.length > 0 ? (
                <span className="iv-h2__count">{invoices.length}</span>
              ) : null}
            </h2>
          </div>

          {loading ? (
            <Skeletons />
          ) : invoices.length === 0 ? (
            <div className="iv-card">
              <Empty title="No invoices yet">
                Once you raise your first invoice it will sit here with a live status, so you always
                know exactly where your money is.
              </Empty>
            </div>
          ) : (
            <div className="iv-card iv-card--flush">
              {invoices.map((invoice) => (
                <InvoiceRow
                  key={invoice.id}
                  invoice={invoice}
                  canResubmit={
                    invoice.status === 'rejected' && Boolean(eligibleById[invoice.campaign_id])
                  }
                  onView={setViewing}
                  onEdit={setEditing}
                  onResubmit={(inv) => setRaising(eligibleById[inv.campaign_id])}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      <InvoiceForm
        open={Boolean(raising)}
        campaign={raising}
        onClose={() => setRaising(null)}
        onSubmitted={(invoice) => {
          setRaising(null);
          toast.success(
            invoice && invoice.notification_sent === false
              ? 'Invoice submitted. Our accounts team will be notified shortly.'
              : 'Invoice submitted and sent to our accounts team.',
          );
          load();
        }}
      />

      <InvoiceForm
        open={Boolean(editing)}
        invoice={editing}
        onClose={() => setEditing(null)}
        onSubmitted={() => {
          setEditing(null);
          toast.success('Invoice updated.');
          load();
        }}
      />

      <InvoiceViewer open={Boolean(viewing)} invoice={viewing} onClose={() => setViewing(null)} />
      <Toaster position="bottom-center" />
    </div>
  );
}
